import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPostById, fetchResourceById } from "../../services/userServices";
import { editpost, uploadFile, editResource } from "../../services/postService"; // adjust path as needed
import ReactQuill from "react-quill";
import imageCompression from "browser-image-compression";
import "react-quill/dist/quill.snow.css";
import {
  FaLink,
  FaQuoteLeft,
  FaTrash,
  FaChevronUp,
  FaChevronDown,
  FaTable,
  FaFile,
} from "react-icons/fa";
import { Image as LucideImage, LetterText } from "lucide-react";
import DashboardStatusGrid from "./DashboardStatusGrid";
import { getLanguage } from "../../services/adminService";
import Spinner from "../shared/Spinner";

const folderKeyMap = {
  news: "Ankuaru_Contents_News",
  tender: "Ankuaru_Contents_Tender",
  event: "Ankuaru_Contents_Event",
};
const MAX_FILE_SIZE = 1024 * 1024;

function EditPost() {
  const { id } = useParams();
  const [actualId, postType] = id.split("_");
  const navigate = useNavigate();

  // Tracking loading, error, and saving state.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ message: "", isError: false });
  const [languages, setLanguages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  // postLanguage and postIsPosted removed - not used

  // Form data for title, source, and type.
  const [formData, setFormData] = useState({
    title: "",
    source: "",
    type: "",
    language: "",
    is_posted: true,
  });

  const [bodyBlocks, setBodyBlocks] = useState([]);
  const [showOptions, setShowOptions] = useState(false);

  const fetchLanguages = async (initialLanguage, initialIsPosted) => {
    try {
      const data = await getLanguage();
      const activeLanguages = data.filter((lang) => lang.is_active);
      setLanguages(activeLanguages);

      const validLanguage = activeLanguages.find(
        (lang) => lang.code === initialLanguage
      );

      setFormData((prev) => ({
        ...prev,
        language: validLanguage
          ? validLanguage.code
          : activeLanguages[0]?.code || "",
        is_posted:
          typeof initialIsPosted === "boolean" ? initialIsPosted : true,
      }));
    } catch (err) {
      console.error("Failed to fetch languages", err);
    }
  };

  // Fetch the post and initialize formData and bodyBlocks.
  useEffect(() => {
    const fetchData = async () => {
      try {
        let response;

        if (postType === "file") {
          response = await fetchResourceById(actualId);
        } else {
          response = await fetchPostById(actualId);
        }

        if (response?.post) {
          const post = response.post;
          let parsedBody = [];
          try {
            parsedBody = JSON.parse(post.body);
          } catch (e) {
            console.error("Failed to parse body content:", e);
          }

          setFormData({
            title: post.title,
            source: post.source,
            type: post.type,
            language: post.language || "", // default from backend
            is_posted: post.is_posted !== undefined ? post.is_posted : true,
          });

          await fetchLanguages(post.language, post.is_posted);

          const convertedBlocks = Array.isArray(parsedBody)
            ? parsedBody.map((block, index) => {
                const uniqueId = block.id || Date.now() + index;
                switch (block.type) {
                  case "paragraph":
                    return {
                      id: uniqueId,
                      type: "text",
                      content: block.text || "",
                    };
                  case "subTopic":
                    return {
                      id: uniqueId,
                      type: "subTopic",
                      content: block.text || "",
                    };
                  case "image":
                    return {
                      id: uniqueId,
                      type: "image",
                      content: block.url || "",
                    };
                  case "link":
                    return {
                      id: uniqueId,
                      type: "link",
                      content: block.url || "",
                    };
                  case "quote":
                    return {
                      id: uniqueId,
                      type: "quote",
                      content: block.text || "",
                    };
                  case "table":
                    return {
                      id: uniqueId,
                      type: "table",
                      content: block.html || "",
                    };
                  case "document":
                    return {
                      id: uniqueId,
                      type: "document",
                      content: block.url || "",
                      name: block.name || "",
                    };
                  default:
                    return { id: uniqueId, ...block };
                }
              })
            : [];

          setBodyBlocks(convertedBlocks);
        } else {
          setError("Post not found");
        }
      } catch (err) {
        console.error(err);
        setError("Error fetching post");
      } finally {
        setLoading(false);
      }
    };

    if (actualId && postType) {
      fetchData();
    }
  }, [actualId, postType]);

  // Handle changes for title, source, and type.
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add a new block of the specified type.
  const addNewBlock = (blockType) => {
    setBodyBlocks((prev) => [
      ...prev,
      { id: Date.now(), type: blockType, content: "" },
    ]);
    setShowOptions(false);
  };

  // Update a specific block's content.
  const updateBlockContent = (id, content) => {
    setBodyBlocks((prev) =>
      prev.map((block) => (block.id === id ? { ...block, content } : block))
    );
  };

  // Remove a block.
  const removeBlock = (id) => {
    setBodyBlocks((prev) => prev.filter((block) => block.id !== id));
    setUploadProgress((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setUploadStatus((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setUploadErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const moveBlock = (id, direction) => {
    setBodyBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx < 0) return prev;
      const swapWith = direction === "up" ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
      return next;
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const renderUploadStatus = (blockId) => {
    const status = uploadStatus[blockId];
    const progress = uploadProgress[blockId] ?? 0;
    const error = uploadErrors[blockId];

    return (
      <>
        {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        {status && (
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded h-2">
              <div
                className={`h-2 rounded ${
                  status === "error" ? "bg-red-500" : "bg-green-500"
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {status === "completed"
                ? "Upload complete"
                : status === "error"
                  ? "Upload failed"
                  : status === "compressing"
                    ? "Compressing..."
                    : status === "idle"
                    ? "Ready to upload"
                    : `Uploading... ${progress}%`}
            </p>
          </div>
        )}
      </>
    );
  };

  // Handle file selection for image blocks.
  const handleImageUpload = async (id, e) => {
    const file = e.target.files[0];
    if (file) {
      let imageFile = file;
      if (file.size > MAX_FILE_SIZE) {
        try {
          setUploadStatus((prev) => ({ ...prev, [id]: "compressing" }));
          setUploadProgress((prev) => ({ ...prev, [id]: 0 }));
          imageFile = await imageCompression(file, {
            maxSizeMB: 1,
            useWebWorker: true,
            onProgress: (percent) => {
              const mapped = Math.min(30, Math.round((percent / 100) * 30));
              setUploadProgress((prev) => ({ ...prev, [id]: mapped }));
            },
          });
        } catch (error) {
          setUploadErrors((prev) => ({
            ...prev,
            [id]: "Image compression failed. Please try another image.",
          }));
          setToast({
            message: "Image compression failed.",
            isError: true,
          });
          e.target.value = "";
          return;
        }
      }
      if (imageFile.size > MAX_FILE_SIZE) {
        setUploadErrors((prev) => ({
          ...prev,
          [id]: "Image is still larger than 1MB after compression.",
        }));
        setToast({
          message: "Image is still larger than 1MB after compression.",
          isError: true,
        });
        e.target.value = "";
        return;
      }
      setUploadErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setUploadProgress((prev) => ({ ...prev, [id]: 0 }));
      setUploadStatus((prev) => ({ ...prev, [id]: "uploading" }));
      const preview = URL.createObjectURL(imageFile);
      setBodyBlocks((prev) =>
        prev.map((block) =>
          block.id === id ? { ...block, content: preview, file: imageFile } : block
        )
      );
      try {
        const uploadResult = await uploadFile(
          imageFile,
          "AnkuaruResources",
          (progress) => {
            const mapped = 30 + Math.round((progress / 100) * 70);
            setUploadProgress((prev) => ({
              ...prev,
              [id]: mapped,
            }));
          }
        );
        setBodyBlocks((prev) =>
          prev.map((block) =>
            block.id === id
              ? { ...block, content: uploadResult.filelink, file: undefined }
              : block
          )
        );
        setUploadStatus((prev) => ({ ...prev, [id]: "completed" }));
        setUploadProgress((prev) => ({ ...prev, [id]: 100 }));
        setToast({ message: "Upload completed.", isError: false });
      } catch (error) {
        setUploadStatus((prev) => ({ ...prev, [id]: "error" }));
        setUploadErrors((prev) => ({
          ...prev,
          [id]: "Upload failed. Please try again.",
        }));
        setToast({ message: "Upload failed. Please try again.", isError: true });
      }
    }
  };

  const handleDocumentUpload = async (id, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadErrors((prev) => ({
          ...prev,
          [id]: "File size must be 1MB or smaller.",
        }));
        setToast({
          message: "File size must be 1MB or smaller.",
          isError: true,
        });
        e.target.value = "";
        return;
      }
      setUploadErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setUploadProgress((prev) => ({ ...prev, [id]: 0 }));
      setUploadStatus((prev) => ({ ...prev, [id]: "uploading" }));
      setBodyBlocks((prev) =>
        prev.map((block) =>
          block.id === id
            ? { ...block, content: URL.createObjectURL(file), file, name: file.name }
            : block
        )
      );
      try {
        const folderKey =
          postType === "file" ? "AnkuaruResources" : folderKeyMap[formData.type];
        const uploadResult = await uploadFile(
          file,
          folderKey,
          (progress) => {
            setUploadProgress((prev) => ({
              ...prev,
              [id]: progress,
            }));
          }
        );
        setBodyBlocks((prev) =>
          prev.map((block) =>
            block.id === id
              ? {
                  ...block,
                  content: uploadResult.filelink,
                  file: undefined,
                  name: file.name,
                }
              : block
          )
        );
        setUploadStatus((prev) => ({ ...prev, [id]: "completed" }));
        setUploadProgress((prev) => ({ ...prev, [id]: 100 }));
        setToast({ message: "Upload completed.", isError: false });
      } catch (error) {
        setUploadStatus((prev) => ({ ...prev, [id]: "error" }));
        setUploadErrors((prev) => ({
          ...prev,
          [id]: "Upload failed. Please try again.",
        }));
        setToast({ message: "Upload failed. Please try again.", isError: true });
      }
    }
  };


  // Conversion function: transforms bodyBlocks array to structured JSON.
  const convertBlocksToStructuredContent = (blocks) => {
    return blocks
      .map((block) => {
        if (block.type === "text" || block.type === "paragraph") {
          return { type: "paragraph", text: block.content || "" };
        } else if (block.type === "image") {
          return {
            type: "image",
            url: block.content || "",
            caption: "",
            alt: "",
          };
        } else if (block.type === "link") {
          return {
            type: "link",
            url: {
              url: block.content?.url || "",
              displayText:
                block.content?.displayText || block.content?.url || "",
            },
          };
        } else if (block.type === "quote") {
          return { type: "quote", text: block.content || "", author: "" };
        } else if (block.type === "table") {
          // grab the HTML you rendered via `block.html`
          return { type: "table", html: block.content || block.html || "" };
        } else if (block.type === "subTopic") {
          return {
            type: "subTopic", // Save as type "subTopic"
            text: block.content,
          };
        } else if (block.type === "document") {
          return {
            type: "document",
            url: block.content,
            name: block.name || block.file?.name || "",
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  useEffect(() => {
    if (!toast.message) return;
    const timer = setTimeout(
      () => setToast({ message: "", isError: false }),
      3000
    );
    return () => clearTimeout(timer);
  }, [toast]);

  // Handle form submission.
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const hasUploads = bodyBlocks.some(
        (block) =>
          (block.type === "image" || block.type === "document") && block.file
      );
      // Process image blocks: upload files and update their content.
      const processedBlocks = await Promise.all(
        bodyBlocks.map(async (block) => {
          const folderKey =
            formData.type === "file"
              ? "AnkuaruResources"
              : folderKeyMap[formData.type];
          if (block.type === "image" && block.file) {
            setUploadStatus((prev) => ({ ...prev, [block.id]: "uploading" }));
            setUploadProgress((prev) => ({ ...prev, [block.id]: 0 }));
            try {
              const uploadResult = await uploadFile(
                block.file,
                folderKey,
                (progress) => {
                  setUploadProgress((prev) => ({
                    ...prev,
                    [block.id]: progress,
                  }));
                }
              );
              setUploadStatus((prev) => ({ ...prev, [block.id]: "completed" }));
              setUploadProgress((prev) => ({ ...prev, [block.id]: 100 }));
              return {
                ...block,
                content: uploadResult.filelink,
                file: undefined,
                name: block.name || block.file.name,
              };
            } catch (error) {
              setUploadStatus((prev) => ({ ...prev, [block.id]: "error" }));
              throw error;
            }
          } else if (block.type === "document" && block.file) {
            setUploadStatus((prev) => ({ ...prev, [block.id]: "uploading" }));
            setUploadProgress((prev) => ({ ...prev, [block.id]: 0 }));
            try {
              const uploadResult = await uploadFile(
                block.file,
                folderKey,
                (progress) => {
                  setUploadProgress((prev) => ({
                    ...prev,
                    [block.id]: progress,
                  }));
                }
              );
              setUploadStatus((prev) => ({ ...prev, [block.id]: "completed" }));
              setUploadProgress((prev) => ({ ...prev, [block.id]: 100 }));
              return {
                ...block,
                content: uploadResult.filelink,
                file: undefined,
                name: block.name || block.file.name,
              };
            } catch (error) {
              setUploadStatus((prev) => ({ ...prev, [block.id]: "error" }));
              throw error;
            }
          }
          return block;
        })
      );
      const structuredContent =
        convertBlocksToStructuredContent(processedBlocks);

      if (formData.type === "file") {
        await editResource(
          id,
          formData.title,
          JSON.stringify(structuredContent),
          formData.source,
          formData.is_posted // boolean: true or false
        );
        setToast({
          message: `${hasUploads ? "Upload completed. " : ""}Resource updated successfully!`,
          isError: false,
        });

        navigate("/ankuaru/previewresource");
      } else {
        await editpost(
          actualId,
          formData.title,
          JSON.stringify(structuredContent),
          formData.source,
          formData.type,
          formData.language,
          formData.is_posted ? 1 : 0
        );

        setToast({
          message: `${hasUploads ? "Upload completed. " : ""}Post updated successfully!`,
          isError: false,
        });

        navigate("/ankuaru/preViewPosts");
      }
    } catch (error) {
      setToast({ message: "Error updating post:", isError: false });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-[calc(100vh-8rem)]">
        <Spinner size="xl" fullScreen={true} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray">
        <div className="container mx-auto max-w-6xl py-12">
          <p className="text-red-500 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray">
      <DashboardStatusGrid />
      <div className="container mx-auto max-w-6xl py-10">
        {/* Card container styled similarly to CreatePost */}
        <div className="bg-transparent rounded-lg">
          {/* Header with title and Cancel button */}
          <div className="flex justify-between items-center mb-8 border-b pb-3">
            <h1 className="text-3xl font-rthin">Edit Post</h1>
            <div className="flex items-center gap-6">
              {/* Toggle Switch: Not Posted / Posted */}
              <div className="flex items-center space-x-3">
                <span
                  className={`text-sm font-medium ${
                    !formData.is_posted ? "text-blue-600" : "text-gray-400"
                  }`}
                >
                  Not Posted
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      is_posted: !prev.is_posted,
                    }))
                  }
                  className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                    formData.is_posted ? "bg-blue-600" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-300 ${
                      formData.is_posted ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </button>
                <span
                  className={`text-sm font-medium ${
                    formData.is_posted ? "text-blue-600" : "text-gray-400"
                  }`}
                >
                  Posted
                </span>
              </div>

              {/* Cancel Button */}
              <button
                onClick={() =>
                  postType === "file"
                    ? navigate("/ankuaru/previewresource")
                    : navigate("/ankuaru/preViewPosts")
                }
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>

          {toast.message && (
            <div
              className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded shadow z-50
      ${
        toast.isError
          ? "bg-red-100 border-red-400 text-red-700"
          : "bg-green-100 border-green-400 text-green-700"
      }`}
            >
              {toast.message}
              <button
                onClick={() => setToast({ message: "", isError: false })}
                className="ml-4 font-bold"
              >
                ×
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title Field */}
            <div>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Title"
                className="w-full px-3 py-2 border-b focus:outline-none focus:border-b-gray-600 bg-gray text-3xl font-rlight"
              />
            </div>

            {/* Source Field */}
            <div>
              <input
                type="text"
                id="source"
                name="source"
                value={formData.source}
                onChange={handleInputChange}
                required
                placeholder="Source"
                className="w-full px-3 py-2 border-b bg-gray text-xl font-rlight focus:outline-none focus:border-b-gray-600"
              />
            </div>

            {/* Type Selector – only show if not a 'file' type */}
            {postType !== "file" && (
              <div className="flex gap-4">
                {/* Type Dropdown */}
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-6 py-1 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-gray-500 bg-gray text-lg text-gray-700"
                >
                  <option value="news">News</option>
                  <option value="tender">Tender</option>
                  <option value="event">Event</option>
                </select>

                {/* Language Dropdown */}
                <select
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  required
                  className="w-full px-6 py-1 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-gray-500 bg-gray text-lg text-gray-700"
                >
                  {languages.map((lang) => (
                    <option key={lang.id} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Custom Body Content Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <div className="border rounded-md p-4">
                {/* Render already added blocks */}
                {bodyBlocks.map((block) => (
                  <div
                    key={block.id}
                    className="relative border rounded-md p-4 mb-4 shadow-md"
                  >
                    {/* Remove Button */}
                    <div className="absolute left-2 top-2">
                      <button
                        type="button"
                        onClick={() => removeBlock(block.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTrash size={16} />
                      </button>
                    </div>
                    {/* Move Controls */}
                    <div className="absolute right-2 top-2 flex flex-col space-y-1">
                      <button
                        type="button"
                        onClick={() => moveBlock(block.id, "up")}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <FaChevronUp size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveBlock(block.id, "down")}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <FaChevronDown size={16} />
                      </button>
                    </div>
                    <div className="ml-16">
                      {block.type === "text" && (
                        <ReactQuill
                          value={block.content || ""}
                          onChange={(content) =>
                            updateBlockContent(block.id, content)
                          }
                          modules={{
                            toolbar: [
                              [{ header: [1, 2, 3, false] }],
                              ["bold", "italic", "underline"],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["clean"],
                            ],
                          }}
                        />
                      )}
                      {block.type === "subTopic" && (
                        // Render subTopic as an input styled as an H3.
                        <input
                          type="text"
                          placeholder="Sub Topic"
                          value={block.content}
                          onChange={(e) =>
                            updateBlockContent(block.id, e.target.value)
                          }
                          className="w-full px-3  font-bold text-xl "
                        />
                      )}
                      {block.type === "table" && (
                        <div
                          className="w-full p-2 border rounded-md bg-gray-100"
                          contentEditable
                          onBlur={(e) =>
                            updateBlockContent(
                              block.id,
                              e.currentTarget.innerHTML
                            )
                          }
                          dangerouslySetInnerHTML={{ __html: block.content }}
                        />
                      )}
                      {block.type === "image" && (
                        <>
                          {block.content ? (
                            <div>
                              <img
                                src={block.content}
                                alt="Preview"
                                className="max-h-64"
                              />
                              {/^https?:\/\//i.test(block.content) && (
                                <a
                                  href={block.content}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block mt-1 text-blue-500 hover:underline text-sm"
                                >
                                  {block.content}
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => removeBlock(block.id)}
                                className="mt-2 text-red-500 text-sm"
                              >
                                Remove Image
                              </button>
                            </div>
                          ) : (
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(block.id, e)}
                            />
                          )}
                          {block.file && (
                            <p className="text-xs text-gray-500 mt-1">
                              Size: {formatFileSize(block.file.size)}
                            </p>
                          )}
                          {renderUploadStatus(block.id)}
                        </>
                      )}
                      {block.type === "link" && (
                        <div className="flex flex-col space-y-2">
                          <input
                            type="text"
                            placeholder="Display Text"
                            value={block.content?.displayText || ""}
                            onChange={(e) =>
                              updateBlockContent(block.id, {
                                ...block.content,
                                displayText: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                          <input
                            type="text"
                            placeholder="Enter URL"
                            value={block.content?.url || ""}
                            onChange={(e) =>
                              updateBlockContent(block.id, {
                                ...block.content,
                                url: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      )}

                      {block.type === "quote" && (
                        <input
                          type="text"
                          placeholder="Enter quote here"
                          value={block.content}
                          onChange={(e) =>
                            updateBlockContent(block.id, e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      )}
                      {block.type === "document" && (
                        <div className="space-y-2">
                          <input
                            type="file"
                            id={`document-${block.id}`}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                            onChange={(e) => handleDocumentUpload(block.id, e)}
                            className="hidden"
                          />
                          <label
                            htmlFor={`document-${block.id}`}
                            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer"
                          >
                            <FaFile className="mr-2" />
                            {block.content
                              ? "Change Document"
                              : "Upload Document"}
                          </label>
                          {block.content && (
                            <div className="mt-2">
                              <a
                                href={block.content}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:underline"
                              >
                              View Document:{" "}
                              {block.name || block.content.split("/").pop()}
                              </a>
                              <button
                                type="button"
                                onClick={() => removeBlock(block.id)}
                                className="mt-1 ml-2 text-red-500 text-sm hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          )}
                          {block.file && (
                            <p className="text-xs text-gray-500">
                              Size: {formatFileSize(block.file.size)}
                            </p>
                          )}
                          {renderUploadStatus(block.id)}
                        </div>
                      )}
                      {block.type === "table" && (
                        <div
                          className="overflow-auto"
                          dangerouslySetInnerHTML={{ __html: block.html }}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {/* New block addition area */}
                <div className="relative border rounded-md p-4">
                  {/* Fixed plus button on the left side */}
                  <div className="absolute left-0 top-0 h-full flex">
                    <button
                      type="button"
                      onClick={() => setShowOptions(!showOptions)}
                      className="w-12 h-full flex items-center justify-center border border-dashed border-gray-400 text-2xl bg-green-500 text-white"
                    >
                      +
                    </button>
                  </div>
                  {/* Content area of the add block section (shifted right) */}
                  <div className="ml-16">
                    {showOptions ? (
                      <div className="mt-2 flex space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            addNewBlock("text");
                            setShowOptions(false);
                          }}
                          className="px-4 py-2 bg-transparent text-gray-500 rounded"
                        >
                          <LetterText size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            addNewBlock("subTopic");
                            setShowOptions(false);
                          }}
                          className="px-4 py-2 bg-transparent text-gray-500 rounded"
                        >
                          Sub
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            addNewBlock("table");
                            setShowOptions(false);
                          }}
                          className="px-4 py-2 bg-transparent text-gray-500 rounded"
                        >
                          <FaTable size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            addNewBlock("image");
                            setShowOptions(false);
                          }}
                          className="px-4 py-2 bg-transparent text-gray-500 rounded"
                        >
                          <LucideImage size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            addNewBlock("link");
                            setShowOptions(false);
                          }}
                          className="px-4 py-2 bg-transparent text-gray-500 rounded"
                        >
                          <FaLink size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            addNewBlock("quote");
                            setShowOptions(false);
                          }}
                          className="px-4 py-2 bg-transparent text-gray-500 rounded"
                        >
                          <FaQuoteLeft size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            addNewBlock("document");
                            setShowOptions(false);
                          }}
                          className="px-4 py-2 bg-transparent text-gray-500 rounded hover:bg-gray-100"
                          title="Add Document"
                        >
                          <FaFile size={20} />
                          <span className="sr-only">Document</span>
                        </button>
                      </div>
                    ) : (
                      <p className="text-gray-400">Tap + to add a body block</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`px-4 py-2 text-white rounded-md ${
                  saving
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditPost;
